/**
 * Lightweight website analytics tracker for Phocea Rent.
 * Sends a beacon to POST /api/analytics/track on every page view.
 *
 * Usage: import { initAnalytics, trackEvent } from '@/lib/analytics';
 */

const SESSION_KEY = 'phocea_analytics_sid';
const LAST_PATH_KEY = 'phocea_analytics_last_path';
const API = `${window.location.origin}/api`;

function getOrCreateSessionId() {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      // Cheap UUID v4-ish using crypto when available
      const rand = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36);
      sid = rand;
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return 'anon-' + Math.random().toString(36).slice(2);
  }
}

function readUtms() {
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get('utm_source') || '',
    utm_medium: p.get('utm_medium') || '',
    utm_campaign: p.get('utm_campaign') || '',
  };
}

/**
 * Fire a page-view beacon.
 * Uses navigator.sendBeacon when possible (survives nav), else fetch with keepalive.
 */
export function trackPageview(customPath) {
  try {
    const path = customPath || window.location.pathname + window.location.search;
    // Dedup: skip if we just tracked the same path <500ms ago (React double-invoke in StrictMode)
    const last = sessionStorage.getItem(LAST_PATH_KEY);
    const now = Date.now();
    if (last) {
      const [lastPath, lastTs] = last.split('|');
      if (lastPath === path && now - parseInt(lastTs, 10) < 500) return;
    }
    sessionStorage.setItem(LAST_PATH_KEY, `${path}|${now}`);

    const body = JSON.stringify({
      session_id: getOrCreateSessionId(),
      path,
      referrer: document.referrer || '',
      ...readUtms(),
      event: 'pageview',
    });
    const url = `${API}/analytics/track`;
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
    } else {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    /* silent — analytics must never break UX */
  }
}

/**
 * Fire a custom event beacon (e.g. 'reservation_started', 'reservation_completed').
 */
export function trackEvent(event, extra = {}) {
  try {
    const body = JSON.stringify({
      session_id: getOrCreateSessionId(),
      path: window.location.pathname,
      referrer: document.referrer || '',
      ...readUtms(),
      event,
      ...extra,
    });
    const url = `${API}/analytics/track`;
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
    } else {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    /* silent */
  }
}

/**
 * Wire up automatic tracking of route changes inside the SPA.
 * Patches pushState/replaceState so every client-side navigation emits a pageview.
 */
export function initAnalytics() {
  if (typeof window === 'undefined') return;
  if (window.__phoceaAnalyticsInit) return;
  window.__phoceaAnalyticsInit = true;

  // First page load
  trackPageview();

  // History API (React Router etc.) — patch to capture nav
  const _push = window.history.pushState;
  const _replace = window.history.replaceState;
  window.history.pushState = function (...args) {
    const res = _push.apply(this, args);
    setTimeout(() => trackPageview(), 0);
    return res;
  };
  window.history.replaceState = function (...args) {
    const res = _replace.apply(this, args);
    setTimeout(() => trackPageview(), 0);
    return res;
  };

  // Back/forward buttons
  window.addEventListener('popstate', () => trackPageview());
}
